
import Vue from "vue";
import dialog from "@/common/mixins/dialog";
import TableCustom from "@/components/TableCustom.vue";
import Container from "@/components/Container.vue";
import LoadingGraph from "@/components/LoadingGraph.vue";
import DeleteIcon from "@/components/icons/DeleteIcon.vue";
import SearchIcon from "@/components/icons/SearchIcon.vue";

import ManageMemberInviteModal from "../components/ManageMemberInviteModal.vue";
import ManageMemberEditModal from "../components/ManageMemberEditModal.vue";

export default Vue.extend({
  name: "ManageMemberPage",
  mixins: [dialog],
  components: {
    TableCustom,
    Container,
    LoadingGraph,
    DeleteIcon,
    SearchIcon,
    ManageMemberInviteModal,
    ManageMemberEditModal,
  },
  data() {
    const memberHeaders = [
      { text: "", value: "profileImageUrl", sortable: false },
      { text: "Name", value: "name", width: "18%" },
      { text: "Email", value: "email", width: "22%" },
      { text: "Role", value: "role", align: "center" },
      { text: "Status", value: "status", align: "center" },
      { text: "Companies", value: "companies", sortable: false, width: "25%" },
      { text: "", value: "action", sortable: false },
    ];

    return {
      isLoading: false,
      isSubmitting: false,
      memberHeaders,
      members: [] as any[],
      companies: [] as any[],
      selectedCompanyIds: [] as number[],
      lastFetchedCompanyIds: [] as number[],
      selectedRole: null as string | null,
      selectedStatus: null as string | null,
      roleOptions: ["owner", "owner_rvs", "member", "supmem"],
      statusOptions: ["active", "pending", "inactive"],
      searchQuery: "",
      showInviteModal: false,
      showEditModal: false,
      selectedMember: null as any,
      debounce: 0 as any,
      currentPage: 1,
      pageSize: 10,
      totalPages: 0,
      totalItems: 0,
      pageSizeOptions: [
        { label: "5", value: 5 },
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
      ],
    };
  },
  async created() {
    const role = this.$store.getters.user?.role?.toUpperCase();
    if (role !== "ADMIN") {
      this.$router.replace({ name: "CompaniesPage" });
      return;
    }

    const companyQuery = this.$route.query.company;
    if (companyQuery) {
      this.selectedCompanyIds = [Number(companyQuery)];
    }

    await this.fetchCompanies();
    await this.fetchMembers();
  },
  methods: {
    async fetchCompanies() {
      try {
        const result = await (this as any).$dep.companyUseCase.getCompanies();
        this.companies = result || [];
      } catch {
        this.companies = [];
      }
    },
    async fetchMembers(silent = false) {
      try {
        if (!silent) this.isLoading = true;
        const response = await (this as any).$dep.manageMemberService.getAllMembers({
          search: this.searchQuery || undefined,
          company_ids: this.selectedCompanyIds.length ? this.selectedCompanyIds : undefined,
          role: this.selectedRole || undefined,
          status: this.selectedStatus || undefined,
          page: this.currentPage,
          size: this.pageSize,
        });
        const items = response.items || [];
        this.members = items;
        this.totalItems = response.total || 0;
        this.totalPages = this.totalItems > 0 ? Math.ceil(this.totalItems / this.pageSize) : 0;
        this.lastFetchedCompanyIds = [...this.selectedCompanyIds];
      } catch {
        this.members = [];
        this.totalItems = 0;
        this.totalPages = 0;
      } finally {
        if (!silent) this.isLoading = false;
      }
    },
    onPageChange() {
      this.fetchMembers(true);
    },
    getCompanyTitle(id: number): string {
      const company = this.companies.find((c: any) => c.id === id);
      return company ? company.title : `#${id}`;
    },
    onRoleFilterChange() {
      this.currentPage = 1;
      this.fetchMembers();
    },
    onStatusFilterChange() {
      this.currentPage = 1;
      this.fetchMembers();
    },
    onCompanyFilterChange() {
      const prev = [...this.lastFetchedCompanyIds].sort().join(",");
      const curr = [...this.selectedCompanyIds].sort().join(",");
      if (prev === curr) return;
      this.currentPage = 1;
      this.fetchMembers();
    },
    onPageSizeChange() {
      this.currentPage = 1;
      this.fetchMembers();
    },
    openEditModal(member: any) {
      this.selectedMember = member;
      this.showEditModal = true;
    },
    async handleInvite(params: { email: string; role: string; company_ids: number[] }) {
      try {
        this.isLoading = true;
        await (this as any).$dep.manageMemberService.globalInviteMember(params);
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: "Invite member successfully",
          type: "success",
        });
        this.currentPage = 1;
        await this.fetchMembers();
      } catch (error: any) {
        (this as any).alertError("Error", error.response?.data?.detail || "Cannot invite member");
      } finally {
        this.isLoading = false;
      }
    },
    async handleEdit(params: any) {
      if (!this.selectedMember) return;
      try {
        this.isSubmitting = true;
        await (this as any).$dep.manageMemberService.globalUpdateMember(this.selectedMember.id, params);
        this.showEditModal = false;
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: "Edit member successfully",
          type: "success",
        });
        await this.fetchMembers();
      } catch (error: any) {
        (this as any).alertError("Error", error.response?.data?.detail || "Cannot edit member");
      } finally {
        this.isSubmitting = false;
      }
    },
    async resendInviteMember(email: string) {
      try {
        await (this as any).$dep.memberUseCase.resendInviteMember(email);
        await (this as any).alertSuccess("Success", "Resend invite completed");
      } catch (error: any) {
        (this as any).alertError("Error", error.response?.data?.detail || "Cannot resend invite");
      }
    },
    async restoreMember(data: any) {
      const { name, email } = data;
      const isConfirmed = await (this as any).alertBasic(
        name,
        `<p>Are you sure you want to activate this member?</p>`,
        "question",
        "Confirm"
      );
      if (isConfirmed) {
        try {
          this.isLoading = true;
          await (this as any).$dep.memberUseCase.activeMember(email);
          await (this as any).alertSuccess("Success", `"${email}" has been activated`);
        } catch (error: any) {
          (this as any).alertError("Error", error.response?.data?.detail || "Cannot activate member");
        } finally {
          this.isLoading = false;
          await this.fetchMembers();
        }
      }
    },
    async deleteMember(email: string) {
      const isConfirmed = await (this as any).alertConfirm("Remove Member", `Are you sure you want to remove ${email}?`);
      if (isConfirmed) {
        try {
          this.isLoading = true;
          await (this as any).$dep.memberUseCase.deleteMember(email);
          await (this as any).alertSuccess("Success", "Member removed successfully");
        } catch (error: any) {
          (this as any).alertError("Error", error.response?.data?.detail || "Cannot remove member");
        } finally {
          this.isLoading = false;
          await this.fetchMembers();
        }
      }
    },
    isOnPermission(status: string, type: string) {
      if (type === "ACTIVE") return ["inactive"].includes(status);
      if (type === "DELETE") return ["active", "pending"].includes(status);
      if (type === "RESEND_INVITE") return ["pending"].includes(status);
      if (type === "EDIT_ROLE") return ["active"].includes(status);
      return false;
    },
  },
  watch: {
    searchQuery() {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        this.currentPage = 1;
        this.fetchMembers();
      }, 600);
    },
  },
});
